import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import InsightsIcon from '@mui/icons-material/Insights';
import FilterBar from '../shared/FilterBar';
import DataTable from '../shared/DataTable';
import Pagination, { normalizePagination } from '../shared/Pagination';
import { buildParams } from '../shared/apiParams';
import config from '../config';

// The Express backend (subhroBackendDashboard), same as every other page —
// served out of its DuckDB database rather than the Kobo-backed
// api/dashboard/[...slug].js functions this used to call.
const API_URL = config.apiUrl;

const TABS = [
  { key: 'enumerator', label: 'By Enumerator', col: 'Enumerator ID' },
  { key: 'block', label: 'By Block', col: 'Block Name' },
  { key: 'gp', label: 'By Gram Panchayat', col: 'Gram Panchayat' },
  { key: 'village', label: 'By Village', col: 'Village Name' },
];

const EMPTY = { blocks: [], gramPanchayats: [], villages: [], enumerators: [], startDate: '', endDate: '' };

const formatDuration = (m) => {
  if (!m || m === 0) return '0 min';
  const h = Math.floor(m / 60);
  const mins = Math.floor(m % 60);
  return h > 0 ? `${h} hr ${mins} min` : `${mins} min`;
};

export default function Analytics() {
  const [activeTab, setActiveTab] = useState('enumerator');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState(normalizePagination({}));
  const [applied, setApplied] = useState(EMPTY);

  const tab = TABS.find((t) => t.key === activeTab);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams(applied, { extra: { page, limit: pageSize } });
      let url;
      if (activeTab === 'enumerator') {
        url = `${API_URL}/analytics/enumerator`;
      } else {
        if (activeTab !== 'block') params.append('forceGroupBy', activeTab);
        url = `${API_URL}/analytics/dynamic`;
      }
      const res = await axios.get(`${url}?${params}`);
      if (res.data?.success) {
        setRows(res.data.data || []);
        setPagination(normalizePagination(res.data.pagination || {}));
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, applied, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const firstColValue = (item) => (activeTab === 'enumerator' ? item.enumerator_id : item.name);

  const columns = [
    { key: 'first', label: tab.col, render: (r) => firstColValue(r) || 'N/A' },
    { key: 'totalSurveys', label: 'Total Surveys', align: 'center' },
    { key: 'avgDur', label: 'Avg Duration', align: 'center', render: (r) => formatDuration(r.avgDurationMinutes) },
    { key: 'medDur', label: 'Median Duration', align: 'center', render: (r) => formatDuration(r.medianDurationMinutes) },
    { key: 'totMem', label: 'Total Members', align: 'center', render: (r) => r.totalHouseholdMembers ?? '—' },
    { key: 'avgMem', label: 'Avg Members', align: 'center', render: (r) => r.avgHouseholdMembers?.toFixed(1) ?? '—' },
    { key: 'medMem', label: 'Median Members', align: 'center', render: (r) => r.medianHouseholdMembers ?? '—' },
    { key: 'totChild', label: 'Total Children', align: 'center', render: (r) => r.totalChildrenReceiving ?? '—' },
    { key: 'avgChild', label: 'Avg Children', align: 'center', render: (r) => r.avgChildrenReceiving?.toFixed(1) ?? '—' },
    { key: 'medChild', label: 'Median Children', align: 'center', render: (r) => r.medianChildrenReceiving ?? '—' },
  ];

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">
          <InsightsIcon /> Analytics
        </h1>
        <p className="page-subtitle">Group-by statistics across enumerator, block, GP and village.</p>
      </div>

      <div className="tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`tab-btn${activeTab === t.key ? ' active' : ''}`}
            onClick={() => {
              if (t.key !== activeTab) {
                setActiveTab(t.key);
                setPage(1);
              }
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <FilterBar
        apiBase={API_URL}
        onApply={(v) => { setApplied(v); setPage(1); }}
        onClear={() => { setApplied(EMPTY); setPage(1); }}
        loading={loading}
      />

      <div className="card">
        <h3 className="card-title">Statistics by {tab.label.replace('By ', '')}</h3>
        <Pagination
          pagination={pagination}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          unitLabel="groups"
          shown={rows.length}
        />
        <DataTable
          columns={columns}
          rows={rows}
          loading={loading}
          emptyMessage='No data found. Adjust filters and click "Apply Filters".'
          getRowKey={(r, i) => `${firstColValue(r)}-${i}`}
        />
      </div>
    </div>
  );
}
